import React, { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useLocation, useParams } from 'react-router-dom';
import { ResidentPageType, ThreeStepsDetails } from '@typings/ResidentTypes';
import { SubmitStatus, SubmitStatusEnum } from '@typings/common';
import useResidentDetailsFetch from '@hooks/useResidentDetailsFetch';
import {
  submitResidentThreeSteps,
  getThreeStepsDetails,
  updateResidentStatus,
} from '@api/resident';
// eslint-disable-next-line import/no-named-as-default
import StepOne from './step-one/index';
// eslint-disable-next-line import/no-cycle
import StepTwo from './step-two/index';
// eslint-disable-next-line import/no-named-as-default
import StepThree from './step-three';
import StepFour from './step-four';
import StepFive from './step-five';
import StepSix from './step-six';
import Navigation from './navigation/Navigation';
import {
  SuccessNotiPositionEnum,
  ShowSuccessNotificationType,
} from './commons';

interface ResidentPageProps {
  type: ResidentPageType;
}

const Resident: React.FC<ResidentPageProps> = ({ type }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('');
  const [refreshLogs, setRefreshLogs] = useState(false);

  const [threeStepsData, setThreeStepsData] = useState<ThreeStepsDetails>({
    step2: [],
    step3: [],
    step4: undefined,
  });
  const [callApiStatus, setCallApiStatus] = useState<SubmitStatus>({
    loading: false,
    submitted: SubmitStatusEnum.IDLE,
    message: '',
  });
  const [showSuccessNotification, setShowSuccessNotification] =
    useState<ShowSuccessNotificationType>({
      status: false,
      btnLoading: false,
      position: SuccessNotiPositionEnum.LAST,
    });

  const [userId, setUserId] = useState<string>('');
  const { id: slug } = useParams<{ id: string }>();
  const { residentDetails, refetch: refetchResident } =
    useResidentDetailsFetch(slug);

  const location = useLocation();

  useEffect(() => {
    const state = location.state as any;
    if (state && state.currentStep !== undefined) {
      setCurrentStep(state.currentStep);
    }
  }, [location.state]);

  useEffect(() => {
    refetchResident();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  useEffect(() => {
    setRole(localStorage.getItem('role') || '');
  }, []);

  const changeStep = (status: string) => {
    window.scrollTo(0, 0);
    if (status === 'prev') {
      setCurrentStep(() => currentStep - 1);
    } else {
      setCurrentStep(() => currentStep + 1);
    }
  };
  const updateMentalAndSelfCompletedStatus = async (
    values: ThreeStepsDetails
  ) => {
    if (slug) {
      try {
        const mentalCapacityStatus: any = values?.step3?.find(
          q => q.key === 'mentalCapacityStatus'
        )?.answer;
        const advanceCarePlan: any = values?.step2?.find(
          q => q.key === 'advanceCarePlan'
        )?.answer;
        if (
          Object.keys(values)[0] === 'step3' &&
          mentalCapacityStatus &&
          mentalCapacityStatus !== ''
        ) {
          await updateResidentStatus(
            slug,
            'mental_capacity',
            mentalCapacityStatus
          );
        } else if (
          Object.keys(values)[0] === 'step2' &&
          advanceCarePlan &&
          advanceCarePlan !== ''
        ) {
          await updateResidentStatus(
            slug,
            'prev_self_completed_ACP',
            advanceCarePlan
          );
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const onSubmitThreeStepsData = async (
    submitValues: ThreeStepsDetails,
    position: SuccessNotiPositionEnum
  ) => {
    setCallApiStatus(prev => ({ ...prev, loading: true }));

    const submitData = {
      user_id: userId,
      share: false,
      advance: {
        data: {
          ...threeStepsData,
          ...submitValues,
        },
      },
    };

    const updateResidentStatusStep234 = async (values: ThreeStepsDetails) => {
      if (slug) {
        try {
          if (Object.keys(values)[0] === 'step3') {
            await updateResidentStatus(slug, 'acp');
          } else if (Object.keys(values)[0] === 'step5') {
            await updateResidentStatus(slug, 'conversations');
          }
        } catch (error) {
          console.error(error);
        }
      }
    };

    try {
      setShowSuccessNotification(prev => ({ ...prev, btnLoading: true }));
      const response = await submitResidentThreeSteps(submitData);
      if (response.status === 'success') {
        setIsFormChanged(false);
        updateResidentStatusStep234(submitValues);
        updateMentalAndSelfCompletedStatus(submitValues);
        setRefreshLogs(prev => !prev);
        setShowSuccessNotification(prev => ({
          ...prev,
          status: true,
          position,
        }));
        setTimeout(() => {
          setShowSuccessNotification(prev => ({
            ...prev,
            status: false,
          }));
        }, 3000);

        if (position === SuccessNotiPositionEnum.LAST) {
          setCallApiStatus(prev => ({
            ...prev,
            submitted: SubmitStatusEnum.SUCCESS,
            message:
              'You can continue editing this page or move to the next section.',
          }));

          setTimeout(() => {
            setCallApiStatus(prev => ({
              ...prev,
              submitted: SubmitStatusEnum.IDLE,
              message: '',
            }));
          }, 3000);
        }

        setTimeout(() => {
          setCallApiStatus(prev => ({
            ...prev,
            submitted: SubmitStatusEnum.IDLE,
            message: '',
          }));
          setShowSuccessNotification(prev => ({
            ...prev,
            status: false,
          }));
        }, 3000);
      }
    } catch (error: any) {
      message.error(error.message);
    } finally {
      setCallApiStatus(prev => ({
        ...prev,
        loading: false,
      }));
      setShowSuccessNotification(prev => ({ ...prev, btnLoading: false }));
    }
  };

  const handleGetResidentDetails = useCallback(async () => {
    if (type === ResidentPageType.EDIT && userId) {
      try {
        const response = await getThreeStepsDetails(userId);
        setThreeStepsData(() => response.data.advance.data);
      } catch (error) {
        console.error(error);
      }
    }
  }, [type, userId, setThreeStepsData]);

  useEffect(() => {
    if (residentDetails) {
      const { profile, id } = residentDetails;
      setUserId(id);
      setFullName(profile.full_name);
    }
  }, [residentDetails]);

  const handleShowStepContent = () => {
    switch (currentStep) {
      case 2:
        return (
          <StepTwo
            role={role}
            slug={slug}
            changeStep={changeStep}
            userId={userId}
            threeStepsData={threeStepsData}
            isFormChanged={isFormChanged}
            setIsFormChanged={setIsFormChanged}
            onSubmitThreeStepsData={onSubmitThreeStepsData}
            handleGetResidentDetails={handleGetResidentDetails}
            residentFullName={fullName}
            callApiStatus={callApiStatus}
            showSuccessNotification={showSuccessNotification}
            setShowSuccessNotification={setShowSuccessNotification}
          />
        );
      case 3:
        return (
          <StepThree
            slug={slug}
            role={role}
            userId={userId}
            residentFullName={fullName}
            changeStep={changeStep}
            threeStepsData={threeStepsData}
            handleGetResidentDetails={handleGetResidentDetails}
            onSubmitThreeStepsData={onSubmitThreeStepsData}
            callApiStatus={callApiStatus}
            showSuccessNotification={showSuccessNotification}
            refreshLogs={refreshLogs}
          />
        );
      case 4:
        return (
          <StepFour
            role={role}
            slug={slug}
            userId={userId}
            changeStep={changeStep}
            threeStepsData={threeStepsData}
            callApiStatus={callApiStatus}
            showSuccessNotification={showSuccessNotification}
            onSubmitThreeStepsData={onSubmitThreeStepsData}
            handleGetResidentDetails={handleGetResidentDetails}
          />
        );
      case 5:
        return (
          <StepFive
            role={role}
            slug={slug}
            changeStep={changeStep}
            threeStepsData={threeStepsData}
            handleGetResidentDetails={handleGetResidentDetails}
          />
        );
      case 6:
        return (
          <StepSix
            role={role}
            slug={slug}
            changeStep={changeStep}
            userId={userId}
          />
        );
      default:
        // Default case will handle both 'currentStep === 1' and any unexpected values
        // eslint-disable-next-line react/jsx-props-no-spreading
        return (
          <StepOne
            role={role}
            changeStep={changeStep}
            type={type}
            residentDetails={residentDetails}
          />
        );
    }
  };

  return (
    <div className="govuk-width-container">
      <Navigation
        type={type}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
      />

      {handleShowStepContent()}
    </div>
  );
};

export default Resident;
